import { Component, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, TfaChallenge } from '../../core/services/auth/auth.service';
import { PopupService } from '../../core/services/popup/popup.service';
import { FormService } from '../../shared/services/form/form.service';
import { LoginService } from './login.service';
import { JwtService } from '../../shared/services/jwt/jwt.service';
import { environment } from 'src/environments/environment';
import { NotificationMessagePopupComponent } from '../../shared/components/notification-message-popup/notification-message-popup.component';
import { MessagePopupComponent } from '../../shared/components/message-popup/message-popup.component';
import { CacheService } from 'src/app/shared/services/cache/cache.service';
import { HeaderService } from 'src/app/core/services/header/header.service';
import { gigyaInfo } from 'src/app/types/gigyaInfo';
import { SELECTED_LANGUAGE_KEY } from 'src/app/types/constants';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.sass'],
    standalone: false
})

export class LoginComponent {

  loginForm: FormGroup;
  otpForm: FormGroup;
  forgotPasswordForm: FormGroup;
  submittedWrongCredentials = false;
  loading = false;
  loginError = '';
  serverError = '';
  resetForm: boolean = false;
  resetUserMail = '';
  successMessage: any;
  responseError: any;
  forgotPasswordError: any;
  
  // TFA properties
  showOtpInput = false;
  tfaChallenge: TfaChallenge | null = null;
  otpError = '';

  constructor(private popupService: PopupService, private authService: AuthService, private formService: FormService,
              private translate: TranslateService, private fb: FormBuilder, private router: Router, 
              private headerService: HeaderService, private cacheService: CacheService,
              private loginService: LoginService, private jwtService: JwtService, private cdr: ChangeDetectorRef) {

    this.loginForm = fb.group({
      'email': ['', Validators.compose([Validators.required, Validators.email])],
      'password': ['', Validators.compose([Validators.required, Validators.minLength(8)])]
    });  
    this.otpForm = fb.group({
      'otpCode': ['', Validators.compose([Validators.required, Validators.pattern(/^\d{6}$/)])]
    });
    this.forgotPasswordForm = fb.group({
      'resetEmail': ['', Validators.compose([Validators.required, Validators.email])]
    })

    // Hide any previous feedback as soon as the user edits the e-mail again
    this.forgotPasswordForm.get('resetEmail')?.valueChanges.subscribe(() => {
      this.clearForgotPasswordMessages();
    });
  }
  onSubmit() {
    if(this.loginForm.valid) {
      this.login();
      return;
    }
    this.submittedWrongCredentials = true;
  }
  navigateToForgotPassword(resetFlag: boolean) {
    this.resetForm = resetFlag;
    this.resetUserMail = '';
    this.loginError = '';
    this.submittedWrongCredentials = false;
    this.forgotPasswordForm.reset();
    this.clearForgotPasswordMessages();
    this.cdr.detectChanges();
  }

  private clearForgotPasswordMessages() {
    this.successMessage = '';
    this.forgotPasswordError = '';
    this.responseError = '';
  }
  navigateToRegistration() {
    this.router.navigate(['/registration']);
    this.closePopup();
  }
  closePopup() {
    this.popupService.closeCurrentDialog();
  }
  login() {
    if(this.loading || this.loginForm.invalid) return; 
    this.loading = true;
    this.loginError = '';
    this.authService.actualLogin(
      {
        email: this.loginForm.get('email')?.value,
        password: this.loginForm.get('password')?.value
      }
    ).subscribe({next: (result: any) => {
        this.loading = false;
        // Check if TFA is required
        if(result && (result as TfaChallenge).requiresTfa) {
          this.tfaChallenge = result as TfaChallenge;
          this.showOtpInput = true;
          this.cdr.detectChanges();
          return;
        }
        // Normal login success
        if(result) {
          this.cdr.detectChanges();
          const isOrgAdmin = this.authService.isOrgAdmin(result);
          const navigate = isOrgAdmin ? '/administration' : '/home';
          if(isOrgAdmin || result.accessIdentifier === 'INDADMIN') { 
            this.showNotificationMessageAndSetGigya(result, navigate);
          } else {
              this.popupService.openDialog(MessagePopupComponent, {
                data: { message: this.translate.instant("USER_AUTHORIZE_MSG") }
              });
          }
        } else {
          this.submittedWrongCredentials = true;
        }
      },
      error: (error: any) => {
        if(!environment.production) {
          console.log(error);
        }
        this.loading = false;
        this.loginForm.reset();

        // Gestion des erreurs selon le code d'erreur
        switch(error?.errorCode) {
          case 0:
            this.loginError = this.translate.instant('GIGYA_0');
            break;
          case 206001:
            this.loginError = this.translate.instant('GIGYA_206001');
            break;
          case 206002:
            this.loginError = this.translate.instant('GIGYA_206002');
            break;
          case 400002:
            this.loginError = this.translate.instant('GIGYA_400002');
            break;
          case 400006:
            this.loginError = this.translate.instant('GIGYA_400006');
            break;
          case 401030:
            this.loginError = this.translate.instant('GIGYA_401030');
            break;
          case 403041:
            this.loginError = this.translate.instant('GIGYA_403041');
            break;
          case 403042:
            this.loginError = this.translate.instant('GIGYA_403042');
            break;
          case 403043:
            this.loginError = this.translate.instant('GIGYA_403043');
            break;
          case 403047:
            this.loginError = this.translate.instant('GIGYA_403047');
            break;
          case 403120:
            this.loginError = this.translate.instant('GIGYA_403120');
            break;
          case 500000:
            this.loginError = this.translate.instant('GIGYA_500000');
            break;
          case 500026:
            this.loginError = this.translate.instant('GIGYA_500026');
            break;
          default:
            this.loginError = this.translate.instant('GIGYA_DEFAULT_ERROR');
            break;
        }
      }
    });
  }
  private showNotificationMessageAndSetGigya(result: gigyaInfo, navigate: string) {
    let selectedLanguage = Object.keys(this.cacheService.getItem(SELECTED_LANGUAGE_KEY)).length > 0 ? this.cacheService.getItem(SELECTED_LANGUAGE_KEY) : this.translate.currentLang;
    this.headerService.getLoginPopupMessage(
      {
        "type": "ASOS",
        "country": result.countryId.toString(),
        "accountType": result.accountId.toString(),
        "displayMode": "ONLINE"
      }, selectedLanguage).subscribe({
        next: (apiMessage: any) => {
          const message = (apiMessage || '').trim();
          if(!message) {
            this.finalizeLogin(result, navigate);
          } else{
            this.popupService.openDialog(NotificationMessagePopupComponent, {
            data: { message, isHtml: true }
          }).afterClosed().subscribe(() => {
            this.finalizeLogin(result, navigate);
          });
          }
        },
        error: () => {
          this.finalizeLogin(result, navigate);
        }
      });
  }

  private finalizeLogin(result: gigyaInfo, navigate: string): void {
    this.loginService.setGigyaResponse(result);
    this.popupService.closeCurrentDialog();
    this.router.navigate([navigate]);
  }

  getErrorMessage(controlName: string) {
    return this.loginForm.get(controlName)?.touched || this.submittedWrongCredentials
    ? this.formService.getErrorMessageForFormControl( controlName, this.loginForm) : '';
  }
  getResetEmailErrorMessage() {
    return this.forgotPasswordForm.get('resetEmail')?.touched || this.submittedWrongCredentials
    ? this.formService.getErrorMessageForFormControl('resetEmail', this.forgotPasswordForm) : '';
  }
  forgotPassword() {
    this.forgotPasswordError = '';
    let userInfo = { "gigya_loginId": this.resetUserMail.toLowerCase() };
    let userData: string = this.jwtService.encode(userInfo, "secret");
    this.loginService.passwordReset(userData).subscribe((response: any) => {
      if(!response.error) {
        this.successMessage = this.translate.instant("MSG_PASSWORD_RESET");
      } else {
          this.responseError = response.errorMessage;
          this.forgotPasswordError = response.errorMessage;
          if(this.responseError.toLowerCase().includes('email does not exist')) {
            this.forgotPasswordError = this.translate.instant("NO_EMAIL_ERROR");
          }
        }
      }, (error: any) => {
        this.forgotPasswordError = this.translate.instant("GIGYA_500000");
      }
    );
  }
  verifyOtp() {
    if(this.loading || this.otpForm.invalid || !this.tfaChallenge) return;
    
    this.loading = true;
    this.otpError = '';
    const otpCode = this.otpForm.get('otpCode')?.value;
    
    this.authService.completeTfaLogin(this.tfaChallenge, otpCode).subscribe({
      next: (user: any) => {
        this.loading = false;
        this.cdr.detectChanges();
        if(user) {
          this.cdr.detectChanges();
          const message = this.translate.instant('AI_NOTIFICATION_POPUP');
          const isOrgAdmin = this.authService.isOrgAdmin(user);
          const navigate = isOrgAdmin ? '/administration' : '/home';
          
          if(isOrgAdmin || user.accessIdentifier === 'INDADMIN') { 
            this.showNotificationMessageAndSetGigya(user, navigate);
          } else {
              this.popupService.openDialog(MessagePopupComponent, {
                data: { message: this.translate.instant("USER_AUTHORIZE_MSG") }
              });
          }
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.otpForm.reset();
        // Handle OTP verification errors
        if(error?.errorCode === 400006 || error?.errorCode === 400009) {
          // 400006: Wrong verification code
          // 400009: Invalid code format
          this.otpError = this.translate.instant('OTP_INVALID_CODE');
        } else if(error?.errorCode === 400011) {
          this.otpError = this.translate.instant('OTP_EXPIRED');
        } else {
          this.otpError = this.translate.instant('GIGYA_DEFAULT_ERROR');
        }
        this.cdr.detectChanges();
      }
    });
  }
  cancelOtp() {
    this.showOtpInput = false;
    this.tfaChallenge = null;
    this.otpForm.reset();
    this.otpError = '';
    this.loginForm.reset();
  }
  getOtpErrorMessage() {
    return this.otpForm.get('otpCode')?.touched
      ? this.formService.getErrorMessageForFormControl('otpCode', this.otpForm) : '';
  }
}